import { z } from 'zod'
import { idSchema } from '../validators'
import { MANAGER_SCOPES } from './dashboard'

/**
 * Per-user UI preferences (I-06, GAP-DD-022). Stored on the server, one row
 * per (user, home org), private to the owner: nobody else can read or write
 * them, not even an investor_admin or a platform admin. Nothing here is
 * authoritative business state; it only picks defaults for screens.
 */

/** Reporting period presets; the report builder's own default is the last 90 days (RPT-016). */
export const REPORT_PERIOD_PRESETS = [
  'last_30_days',
  'last_90_days',
  'last_12_months',
  'year_to_date',
] as const
export type ReportPeriodPreset = (typeof REPORT_PERIOD_PRESETS)[number]

export const TABLE_DENSITIES = ['comfortable', 'compact'] as const
export type TableDensity = (typeof TABLE_DENSITIES)[number]

/** Every key is optional: an absent key means "use the screen's default". */
export const preferencesSchema = z.strictObject({
  /** Investor: the portfolio the Portfolio screen opens on (a portfolio id in the caller's org). */
  default_portfolio_id: idSchema.nullable().optional(),
  default_report_period: z.enum(REPORT_PERIOD_PRESETS).optional(),
  table_density: z.enum(TABLE_DENSITIES).optional(),
  /** Staff: the dashboard toggle "My cases" (`mine`) / "All managers" (`all`). */
  staff_dashboard_scope: z.enum(MANAGER_SCOPES).optional(),
})
export type Preferences = z.infer<typeof preferencesSchema>

/**
 * `PUT /preferences` replaces the whole object. `revision` is the revision the
 * client read: 0 when `GET` returned no stored preferences yet.
 */
export const preferencesPutSchema = z.strictObject({
  revision: z.number().int().min(0),
  preferences: preferencesSchema,
})
export type PreferencesPut = z.infer<typeof preferencesPutSchema>

export const preferencesResponseSchema = z.object({
  preferences: preferencesSchema,
  /** 0 until the first save. */
  revision: z.number().int().min(0),
  updated_at: z.string().nullable(),
})
export type PreferencesResponse = z.infer<typeof preferencesResponseSchema>
